/**
 * PURPOSE: Pedir al backend el PDF de resultados por fases de UN deporte colectivo (FTR-0067).
 * RESPONSIBILITIES: POST /resultados-fases-pdf como binario (cancelable con AbortSignal); devolver bytes, nombre de
 *   archivo y tablas omitidas (cabecera X-Results-Omitted); traducir el error JSON que llega dentro del arraybuffer.
 * NOTES: Usa el axios global (mismos interceptores Bearer/401/compañía que httpService), como la descarga masiva de
 *   programación. La descarga de varios deportes y el ZIP los arma DescargarResultadosPdfModal.
 * ROUTE: winscore/src/services/resultadosFasesPdfService.js
 */
import axios from "axios";
import API_BASE_URL from "../config/config.js";

/** Motivo -> texto para el resumen ("Fútbol Femenino: sin tabla acumulada (un solo grupo)"). */
export const OMITTED_TABLE_LABELS = Object.freeze({
  "standings:no_group_phase": "sin tabla de posiciones (no tiene fase de grupos)",
  "accumulated:disabled": "sin tabla acumulada (no está activada en la categoría)",
  "accumulated:single_group": "sin tabla acumulada (un solo grupo)",
  "accumulated:unsupported_sport": "sin tabla acumulada (el deporte no la tiene)",
  "finalStandings:not_defined": "sin ubicación final (aún no definida)",
});

export const omittedLabel = ({ key, reason }) => OMITTED_TABLE_LABELS[`${key}:${reason}`] || `sin ${key} (${reason})`;

const filenameFrom = (disposition, fallback) => {
  const match = String(disposition || "").match(/filename="?([^";]+)"?/);
  return match ? match[1].trim() : fallback;
};

export const parseOmittedHeader = (value) =>
  !value || value === "none"
    ? []
    : String(value)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
        .map((item) => {
          const [key, reason = ""] = item.split(":");
          return { key, reason };
        });

/**
 * @returns {Promise<{ data: Uint8Array, filename: string, omitted: Array<{key: string, reason: string}>, standingsSync: string }>}
 */
export async function downloadResultadosFasesPdf({ idevent, idsport, groups, finals, include, design, signal }) {
  const response = await axios.post(
    `${API_BASE_URL}/resultados-fases-pdf`,
    { idevent: Number(idevent), idsport: Number(idsport), groups: Boolean(groups), finals: Boolean(finals), include, design },
    { responseType: "arraybuffer", signal }
  );
  const headers = response.headers || {};
  if (!String(headers["content-type"] || "").startsWith("application/pdf")) {
    throw new Error("El servidor no devolvió un PDF.");
  }
  return {
    data: new Uint8Array(response.data),
    filename: filenameFrom(headers["content-disposition"], `Resultados_${idsport}.pdf`),
    omitted: parseOmittedHeader(headers["x-results-omitted"]),
    standingsSync: headers["x-standings-sync"] || "n/a",
  };
}

const decodeUtf8 = (bytes) => {
  if (typeof TextDecoder !== "undefined") return new TextDecoder("utf-8").decode(bytes);
  return decodeURIComponent(Array.from(bytes, (b) => `%${b.toString(16).padStart(2, "0")}`).join(""));
};

export const isCancelledRequest = (error) => axios.isCancel?.(error) || error?.code === "ERR_CANCELED" || error?.name === "CanceledError";

/** Mensaje del backend ({ code, msg }) aunque la respuesta haya llegado como arraybuffer. */
export function resultadosPdfErrorMessage(error, fallback = "No se pudo generar el PDF.") {
  const data = error?.response?.data;
  try {
    if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
      const parsed = JSON.parse(decodeUtf8(data instanceof ArrayBuffer ? new Uint8Array(data) : new Uint8Array(data.buffer, data.byteOffset, data.byteLength)));
      return parsed?.msg || parsed?.message || fallback;
    }
  } catch {
    return fallback;
  }
  if (data?.msg || data?.message) return data.msg || data.message;
  // errores propios (p. ej. "El servidor no devolvió un PDF."); los de red de axios vienen en inglés: texto genérico
  if (!error?.isAxiosError && error?.message) return error.message;
  return fallback;
}
